import React, { useEffect, useRef, useState } from 'react';
import {
  Mail, Printer, MessageSquare, MoreHorizontal, ChevronDown, Plus,
  Radio, Map, Users, Search, X, CalendarDays, Navigation, Lightbulb,
  Sparkles, Calendar, Layout, Send
} from 'lucide-react';
import DispatchDateNav from './DispatchDateNav';
import './DispatchTopBar.css';

function DispatchTopBar({
  // date
  selectedDate, onDateChange,
  // view toggle
  view, setView,
  // search
  searchQuery, setSearchQuery,
  // team filter
  selectedTeamFilter, setSelectedTeamFilter,
  showTeamFilter, setShowTeamFilter,
  todayTeamMembers, hoursPerMember, getTeamColor,
  // action handlers / counts
  pendingReminderCount,
  dispatchLogCount,
  onOpenBatchReminders,
  onOpenActivityLog,
  onPrint,
  onQuickAdd,
  // more menu
  showMoreActions, setShowMoreActions,
  onOpenShiftScheduler,
  onCalculateRoutes,
  isCalculatingRoutes,
  onOptimizeRoutes,
  onOpenAutoGroup,
  onOpenTimeOff,
  onSendRoutesToSlack,
  isSendingRoutes,
  // density
  density, onToggleDensity,
}) {
  const searchRef = useRef(null);
  const [searchExpanded, setSearchExpanded] = useState(false);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === '/') {
        e.preventDefault();
        setSearchExpanded(true);
        setTimeout(() => searchRef.current?.focus(), 0);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const showExpanded = searchExpanded || !!searchQuery;

  return (
    <div className="dispatch-topbar">
      <div className="topbar-left">
        <DispatchDateNav selectedDate={selectedDate} onDateChange={onDateChange} compact />
      </div>

      <div className="topbar-center">
        <div className="view-toggle compact">
          <button
            className={`toggle-btn ${view === 'live' ? 'active' : ''}`}
            onClick={() => setView('live')}
            title="Live tracking"
          >
            <Radio size={16} /> Live
          </button>
          <button
            className={`toggle-btn ${view === 'map' ? 'active' : ''}`}
            onClick={() => setView('map')}
            title="Map + routes"
          >
            <Map size={16} /> Map
          </button>
        </div>

        <div className={`topbar-search ${showExpanded ? 'expanded' : ''}`}>
          <Search size={16} />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search jobs (/)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchExpanded(true)}
            onBlur={() => { if (!searchQuery) setSearchExpanded(false); }}
          />
          {searchQuery && (
            <button className="topbar-search-clear" onClick={() => setSearchQuery('')} title="Clear search">
              <X size={14} />
            </button>
          )}
        </div>

        <div className="filter-dropdown">
          <button
            className={`filter-btn ${selectedTeamFilter ? 'active' : ''}`}
            onClick={() => setShowTeamFilter(!showTeamFilter)}
            title="Filter by team member"
          >
            <Users size={14} />
            <span className="filter-btn-label">{selectedTeamFilter || 'All Teams'}</span>
            {selectedTeamFilter && (
              <span className="clear-filter" onClick={(e) => { e.stopPropagation(); setSelectedTeamFilter(null); }}>
                <X size={12} />
              </span>
            )}
          </button>
          {showTeamFilter && (
            <div className="filter-menu">
              <button onClick={() => { setSelectedTeamFilter(null); setShowTeamFilter(false); }}>All Teams</button>
              {todayTeamMembers.map(name => (
                <button key={name} onClick={() => { setSelectedTeamFilter(name); setShowTeamFilter(false); }}>
                  <span className="team-dot" style={{ background: getTeamColor(name) }} />
                  {name}
                  <span className="hours-badge">{hoursPerMember[name] || 0}h</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="topbar-actions">
        <button
          className="topbar-icon-btn"
          onClick={onOpenBatchReminders}
          title="Send appointment reminders"
          aria-label="Send appointment reminders"
        >
          <Mail size={16} />
          {pendingReminderCount > 0 && <span className="log-count-badge">{pendingReminderCount}</span>}
        </button>
        <button
          className="topbar-icon-btn"
          onClick={onPrint}
          title="Print dispatch sheet (P)"
          aria-label="Print dispatch sheet"
        >
          <Printer size={16} />
        </button>
        <button
          className="topbar-icon-btn"
          onClick={onOpenActivityLog}
          title="Activity log"
          aria-label="Activity log"
        >
          <MessageSquare size={16} />
          {dispatchLogCount > 0 && <span className="log-count-badge">{dispatchLogCount}</span>}
        </button>

        <div className="more-actions-wrapper">
          <button
            className="topbar-icon-btn"
            onClick={() => setShowMoreActions(!showMoreActions)}
            title="More actions"
            aria-label="More actions"
          >
            <MoreHorizontal size={16} />
            <ChevronDown size={12} />
          </button>
          {showMoreActions && (
            <>
              <div className="more-actions-backdrop" onClick={() => setShowMoreActions(false)} />
              <div className="more-actions-menu">
                <button onClick={() => { onOpenShiftScheduler(); setShowMoreActions(false); }}>
                  <CalendarDays size={16} /> Shift Schedule
                </button>
                <button onClick={() => { onCalculateRoutes(); setShowMoreActions(false); }} disabled={isCalculatingRoutes}>
                  <Navigation size={16} /> {isCalculatingRoutes ? 'Calculating...' : 'Calculate Routes'}
                </button>
                <button onClick={() => { onOptimizeRoutes(); setShowMoreActions(false); }}>
                  <Lightbulb size={16} /> Optimize Routes
                </button>
                <button onClick={() => { onOpenAutoGroup(); setShowMoreActions(false); }}>
                  <Sparkles size={16} /> Auto-Group Jobs
                </button>
                <button onClick={() => { onOpenTimeOff(); setShowMoreActions(false); }}>
                  <Calendar size={16} /> Time Off Requests
                </button>
                {onSendRoutesToSlack && (
                  <button
                    onClick={() => { onSendRoutesToSlack(); setShowMoreActions(false); }}
                    disabled={isSendingRoutes}
                  >
                    <Send size={16} /> {isSendingRoutes ? 'Sending...' : 'Send Routes to Slack'}
                  </button>
                )}
                <div className="more-actions-divider" />
                <button onClick={() => { onToggleDensity(); setShowMoreActions(false); }}>
                  <Layout size={16} /> {density === 'compact' ? 'Comfortable density' : 'Compact density'}
                </button>
              </div>
            </>
          )}
        </div>

        <button className="btn btn-primary topbar-primary" onClick={onQuickAdd}>
          <Plus size={16} /> Quick Add
        </button>
      </div>
    </div>
  );
}

export default DispatchTopBar;
